import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import { trackLanguageChange } from '../hooks/useAnalytics';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const location = useLocation();
  const { language, switchLanguage, paths } = useLanguage();
  const t = translations[language];

  const navLinks = [
    { path: paths.home, label: t.nav.home },
    { path: paths.about, label: t.nav.about },
    { path: paths.services, label: t.nav.services },
    { path: paths.cases, label: t.nav.cases },
    { path: paths.contact, label: t.nav.contact }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
      <div className="max-w-[1920px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to={paths.home} className="flex items-center">
            <img 
              src="https://i.ibb.co/KcQQXMz3/logo-02.png" 
              alt="Pik Agency" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-base font-medium transition-all duration-200 relative ${
                  isActive(link.path)
                    ? 'text-[#ff5200]'
                    : 'text-black hover:text-[#ff5200]'
                }`}
              >
                {link.label}
                {isActive(link.path) && (
                  <motion.span
                    layoutId="underline"
                    className="absolute left-0 right-0 -bottom-1 h-0.5 bg-[#ff5200]"
                  />
                )}
              </Link>
            ))}
            
            {/* Book Call Button */}
            <a
              href="https://calendar.app.google/1qFnUckG57AkLXAi8"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#ff5200] text-white px-6 py-2.5 rounded-full font-semibold text-sm hover:bg-[#e64a00] transition-colors duration-300"
            >
              Book Call
            </a>
            
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-2 text-black hover:text-[#ff5200] transition-colors p-2"
                aria-label="Change language"
                data-testid="language-toggle-btn"
              >
                <Globe size={20} />
                <span className="text-sm font-medium uppercase">{language}</span>
              </button>
              
              <AnimatePresence>
                {langMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden"
                  >
                    <button
                      onClick={() => {
                        switchLanguage('en');
                        trackLanguageChange('en');
                        setLangMenuOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors ${
                        language === 'en' ? 'bg-[#ff5200] text-white' : 'text-black'
                      }`}
                      data-testid="lang-switch-en-desktop"
                    >
                      English
                    </button>
                    <button
                      onClick={() => {
                        switchLanguage('de');
                        trackLanguageChange('de');
                        setLangMenuOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors ${
                        language === 'de' ? 'bg-[#ff5200] text-white' : 'text-black'
                      }`}
                      data-testid="lang-switch-de-desktop"
                    >
                      Deutsch
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-black hover:text-[#ff5200] transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-gray-100"
          >
            <nav className="px-6 py-4 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block text-lg font-medium transition-colors ${
                    isActive(link.path)
                      ? 'text-[#ff5200]'
                      : 'text-black hover:text-[#ff5200]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Book Call Button - Mobile */}
              <a
                href="https://calendar.app.google/1qFnUckG57AkLXAi8"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full bg-[#ff5200] text-white text-center px-6 py-3 rounded-full font-semibold hover:bg-[#e64a00] transition-colors duration-300"
              >
                Book Call
              </a>
              
              {/* Mobile Language Switcher */}
              <div className="pt-4 border-t border-gray-200">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-3 flex items-center gap-2">
                  <Globe size={16} />
                  Language
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      switchLanguage('en');
                      trackLanguageChange('en');
                      setMobileMenuOpen(false);
                    }}
                    className={`flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                      language === 'en'
                        ? 'bg-[#ff5200] text-white shadow-md'
                        : 'bg-gray-100 text-black hover:bg-gray-200'
                    }`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => {
                      switchLanguage('de');
                      trackLanguageChange('de');
                      setMobileMenuOpen(false);
                    }}
                    className={`flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                      language === 'de'
                        ? 'bg-[#ff5200] text-white shadow-md'
                        : 'bg-gray-100 text-black hover:bg-gray-200'
                    }`}
                  >
                    Deutsch
                  </button>
                </div>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;